import { sendCapRequest, parseCapResponse } from './request';
import {
  ICreateSessionRequest,
  ICreateSessionResponse,
  ISessionData,
  IUpdateSessionRequest,
  IUpdateSessionResponse,
} from './types';

/**
 * CAP Session API methods
 * Handles session creation, retrieval, and updates
 */
export const session = {
  /**
   * Creates a new payment session
   * Requires JWT authentication
   * 
   * @param data - Session creation data
   * @param env - Environment (dev, staging, prod)
   * @returns Session ID
   */
  createSession: async (data: ICreateSessionRequest, env?: string): Promise<ICreateSessionResponse> => {
    const response = await sendCapRequest('psp/cap/session', 'POST', data, undefined, {}, env);
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || `HTTP ${response.status}: ${response.statusText}`);
    }
    
    // Backend returns session_id as plain text, not JSON
    const sessionId = await response.text();
    return { session_id: sessionId };
  },

  /**
   * Retrieves session information
   * Requires session ID header
   * 
   * @param sessionId - Session identifier
   * @param env - Environment (dev, staging, prod)
   * @returns Complete session data
   */
  getSession: async (sessionId: string, env?: string): Promise<ISessionData> => {
    const response = await sendCapRequest('psp/cap/session', 'GET', null, sessionId, {}, env);
    return parseCapResponse<ISessionData>(response);
  },

  /**
   * Updates session data (customer info, checkout data, or merchant flow status)
   * Requires session ID header
   * 
   * @param sessionId - Session identifier
   * @param data - Data to update
   * @param env - Environment (dev, staging, prod)
   * @returns Success status
   */
  updateSession: async (
    sessionId: string,
    data: IUpdateSessionRequest,
    env?: string
  ): Promise<IUpdateSessionResponse> => {
    const response = await sendCapRequest('psp/cap/session', 'PATCH', data, sessionId, {}, env);
    return parseCapResponse<IUpdateSessionResponse>(response);
  },
};

